<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  fetchAdCreativeById,
  fetchAdCreativeStats,
} from "@/services/backofficeClient";
import { placementLabel } from "@/services/adPlacements";
import { DEFAULT_TIMEZONE } from "@/views/analysis/analysisOptions";
import { toDateRangeParams } from "@/views/analysis/analysisFormat";
import AdStatCard from "@/views/adStats/components/AdStatCard.vue";
import AdStatsFilters from "@/views/adStats/components/AdStatsFilters.vue";
import {
  CHART_COLORS,
  CHART_VIEWS,
  buildTimelineChart,
  buildTimelinePoints,
} from "@/views/adStats/adStatsCharts";
import {
  MAX_HOURLY_RANGE_DAYS,
  dayKeyInTimezone,
  defaultGranularityForPreset,
  isDayKey,
  isPresetKey,
  isValidTimezone,
  previousRange,
  rangeDayCount,
  resolvePresetRange,
} from "@/views/adStats/adStatsTime";
import {
  computeDelta,
  formatCount,
  formatDateTime,
  formatDayLabel,
  formatDecimal,
  formatDurationMs,
  formatPercent,
} from "@/views/adStats/adStatsFormat";

const DEFAULT_PRESET = "7d";

const route = useRoute();
const router = useRouter();
const store = useStore();

const creativeId = computed(() => String(route.params.id || ""));
const creative = ref(null);
const creativeLoading = ref(true);
const creativeError = ref("");

const ready = ref(false);
const preset = ref(DEFAULT_PRESET);
const dateFrom = ref("");
const dateTo = ref("");
const timezone = ref(DEFAULT_TIMEZONE);
const granularity = ref("day");
const compare = ref(true);

const stats = ref(null);
const statsLoading = ref(false);
const statsError = ref("");
const statsVersion = ref(0);
const chartView = ref(CHART_VIEWS[0].key);
let statsRequestSequence = 0;

const errorMessageOf = (error, fallback) =>
  error?.payload?.message || error?.message || fallback;

const firstDay = computed(() =>
  creative.value
    ? dayKeyInTimezone(new Date(creative.value.createdAt), timezone.value)
    : "",
);

const localizedTitle = (value) => {
  if (typeof value === "string") return value;
  const map = value && typeof value === "object" ? value : {};
  return (
    map[store.getters.preferredLanguage] ||
    Object.values(map).find((text) => text) ||
    ""
  );
};

const creativeTitle = computed(
  () => localizedTitle(creative.value?.content?.title) || "Untitled ad",
);

const mediaPreviewUrl = computed(() => {
  const media = creative.value?.content?.media;
  return media?.type === "image" && /^https?:\/\//i.test(media.url || "")
    ? media.url
    : "";
});

const flightLabel = computed(() => {
  const { startsAt, endsAt } = creative.value || {};
  if (!startsAt && !endsAt) return "Always on";
  return `${formatDateTime(startsAt)} → ${formatDateTime(endsAt)}`;
});

const restoreFilters = () => {
  const { query } = route;

  timezone.value = isValidTimezone(query.tz) ? query.tz : DEFAULT_TIMEZONE;

  const customRangeRestored =
    query.range === "custom" &&
    isDayKey(query.from) &&
    isDayKey(query.to) &&
    query.from <= query.to;
  preset.value = customRangeRestored
    ? "custom"
    : isPresetKey(query.range) && query.range !== "custom"
      ? query.range
      : DEFAULT_PRESET;

  const range = customRangeRestored
    ? { dateFrom: query.from, dateTo: query.to }
    : resolvePresetRange(preset.value, timezone.value, firstDay.value);
  dateFrom.value = range.dateFrom;
  dateTo.value = range.dateTo;

  const requestedGranularity = ["hour", "day"].includes(query.gran)
    ? query.gran
    : defaultGranularityForPreset(preset.value);
  granularity.value =
    requestedGranularity === "hour" &&
    rangeDayCount(dateFrom.value, dateTo.value) > MAX_HOURLY_RANGE_DAYS
      ? "day"
      : requestedGranularity;

  compare.value = preset.value !== "all" && query.compare !== "0";
};

const syncUrl = () => {
  router.replace({
    query: {
      range: preset.value,
      ...(preset.value === "custom"
        ? { from: dateFrom.value, to: dateTo.value }
        : {}),
      tz: timezone.value,
      gran: granularity.value,
      compare: compare.value ? "1" : "0",
    },
  });
};

const loadCreative = async () => {
  creativeLoading.value = true;
  creativeError.value = "";
  try {
    creative.value = await fetchAdCreativeById(creativeId.value);
  } catch (error) {
    console.error("Failed to fetch ad creative", error);
    creativeError.value = errorMessageOf(error, "Failed to load the ad.");
  } finally {
    creativeLoading.value = false;
  }
};

const loadStats = async () => {
  const sequence = ++statsRequestSequence;
  statsLoading.value = true;
  statsError.value = "";
  try {
    const payload = await fetchAdCreativeStats(creativeId.value, {
      ...toDateRangeParams(dateFrom.value, dateTo.value, timezone.value),
      timezone: timezone.value,
      granularity: granularity.value,
      compare: compare.value,
    });
    if (sequence !== statsRequestSequence) return;
    stats.value = payload;
    statsVersion.value += 1;
  } catch (error) {
    if (sequence !== statsRequestSequence) return;
    console.error("Failed to fetch ad statistics", error);
    statsError.value = errorMessageOf(error, "Failed to load ad statistics.");
  } finally {
    if (sequence === statsRequestSequence) statsLoading.value = false;
  }
};

const totals = computed(() => stats.value?.totals ?? null);
const previousTotals = computed(() => stats.value?.previousTotals ?? null);

const showRewards = computed(
  () =>
    Boolean(creative.value?.placements?.includes("matchRewarded")) ||
    (totals.value?.rewardsEarned ?? 0) > 0,
);

const hasEvents = computed(() => {
  const current = totals.value;
  if (!current) return false;
  return (
    current.impressions +
      current.clicks +
      current.rewardsEarned +
      current.dismissals +
      current.errors >
    0
  );
});

const periodLabel = computed(() => {
  if (!dateFrom.value || !dateTo.value) return "";
  return dateFrom.value === dateTo.value
    ? formatDayLabel(dateFrom.value)
    : `${formatDayLabel(dateFrom.value)} – ${formatDayLabel(dateTo.value)}`;
});

const previousPeriodLabel = computed(() => {
  if (!previousTotals.value) return "";
  const previous = previousRange(dateFrom.value, dateTo.value);
  return previous.dateFrom === previous.dateTo
    ? formatDayLabel(previous.dateFrom)
    : `${formatDayLabel(previous.dateFrom)} – ${formatDayLabel(previous.dateTo)}`;
});

const deltaOf = (key, options) =>
  totals.value && previousTotals.value
    ? computeDelta(totals.value[key], previousTotals.value[key], options)
    : null;

const kpiCards = computed(() => {
  const current = totals.value;
  if (!current) return [];

  return [
    {
      key: "impressions",
      label: "Impressions",
      value: formatCount(current.impressions),
      hint: `${formatCount(current.uniqueReach)} unique users`,
      accent: CHART_COLORS.impressions,
      delta: deltaOf("impressions"),
      goodWhen: "up",
    },
    {
      key: "clicks",
      label: "Clicks",
      value: formatCount(current.clicks),
      hint: `${formatCount(current.uniqueClickers)} unique users`,
      accent: CHART_COLORS.clicks,
      delta: deltaOf("clicks"),
      goodWhen: "up",
    },
    {
      key: "ctr",
      label: "Click-through rate",
      value: formatPercent(current.clickThroughRate),
      hint: "clicks ÷ impressions",
      accent: CHART_COLORS.clicks,
      delta: deltaOf("clickThroughRate", { asPoints: true }),
      goodWhen: "up",
    },
    ...(showRewards.value
      ? [
          {
            key: "rewards",
            label: "Rewards earned",
            value: formatCount(current.rewardsEarned),
            hint: `${formatCount(current.verifiedRewards)} verified`,
            accent: CHART_COLORS.rewards,
            delta: deltaOf("rewardsEarned"),
            goodWhen: "up",
          },
        ]
      : []),
    {
      key: "dismissals",
      label: "Dismissals",
      value: formatCount(current.dismissals),
      hint: `${formatPercent(current.dismissalRate)} of impressions`,
      accent: CHART_COLORS.dismissals,
      delta: deltaOf("dismissals"),
      goodWhen: "down",
    },
    {
      key: "errors",
      label: "Errors",
      value: formatCount(current.errors),
      hint: `${formatPercent(current.errorRate)} error rate`,
      accent: CHART_COLORS.errors,
      delta: deltaOf("errors"),
      goodWhen: "down",
    },
  ];
});

const outcomeRows = computed(() => {
  const current = totals.value;
  if (!current) return [];

  return [
    {
      key: "clicks",
      label: "Clicks",
      count: current.clicks,
      rate: current.clickThroughRate,
      basis: "of impressions",
      color: CHART_COLORS.clicks,
    },
    ...(showRewards.value
      ? [
          {
            key: "rewards",
            label: "Rewards earned",
            count: current.rewardsEarned,
            rate: current.rewardRate,
            basis: "of impressions",
            color: CHART_COLORS.rewards,
          },
        ]
      : []),
    {
      key: "dismissals",
      label: "Dismissals",
      count: current.dismissals,
      rate: current.dismissalRate,
      basis: "of impressions",
      color: CHART_COLORS.dismissals,
    },
    {
      key: "errors",
      label: "Errors",
      count: current.errors,
      rate: current.errorRate,
      basis: "of impressions + errors",
      color: CHART_COLORS.errors,
    },
  ];
});

const audienceRows = computed(() => {
  const current = totals.value;
  if (!current) return [];

  return [
    {
      label: "Unique reach",
      value: formatCount(current.uniqueReach),
      description: "Distinct users who saw the ad",
    },
    {
      label: "Unique clickers",
      value: formatCount(current.uniqueClickers),
      description: "Distinct users who clicked",
    },
    {
      label: "Unique CTR",
      value: formatPercent(current.uniqueClickThroughRate),
      description: "Unique clickers ÷ unique reach",
    },
    {
      label: "Avg. frequency",
      value: formatDecimal(current.averageFrequency, 2),
      description: "Impressions per reached user",
    },
    {
      label: "Avg. view time",
      value: formatDurationMs(current.averageViewMs),
      description: "Time on screen per impression",
    },
    ...(showRewards.value
      ? [
          {
            label: "Verified rewards",
            value: `${formatCount(current.verifiedRewards)} / ${formatCount(current.rewardsEarned)}`,
            description: "Rewards where watch time met the minimum",
          },
          {
            label: "Avg. watch time",
            value: formatDurationMs(current.averageWatchMs),
            description: "Time watched per earned reward",
          },
        ]
      : []),
  ];
});

const placementRows = computed(() => {
  const totalImpressions = totals.value?.impressions ?? 0;
  return (stats.value?.placements ?? []).map((row) => ({
    ...row,
    label: placementLabel(row.placement),
    share: totalImpressions === 0 ? 0 : row.impressions / totalImpressions,
  }));
});

const availableChartViews = computed(() =>
  CHART_VIEWS.filter((view) => view.key !== "rewards" || showRewards.value),
);

const timelinePoints = computed(() =>
  stats.value
    ? buildTimelinePoints({
        timeline: stats.value.timeline,
        range: stats.value.range,
      })
    : [],
);

const chart = computed(() =>
  buildTimelineChart(chartView.value, timelinePoints.value),
);

const chartKey = computed(() => `${chartView.value}-${statsVersion.value}`);

watch(availableChartViews, (views) => {
  if (!views.some((view) => view.key === chartView.value)) {
    chartView.value = CHART_VIEWS[0].key;
  }
});

watch(
  [ready, preset, dateFrom, dateTo, timezone, granularity, compare],
  () => {
    if (!ready.value) return;
    syncUrl();
    loadStats();
  },
);

onMounted(async () => {
  await loadCreative();
  if (!creative.value) return;
  restoreFilters();
  ready.value = true;
});
</script>

<template>
  <div class="py-4 container-fluid ad-stats">
    <div v-if="creativeLoading" class="card shadow-sm">
      <div class="card-body text-center py-5">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 mb-0 text-sm text-secondary">Loading ad...</p>
      </div>
    </div>

    <div v-else-if="creativeError" class="card shadow-sm">
      <div class="card-body text-center py-5">
        <p class="text-sm mb-3">{{ creativeError }}</p>
        <router-link :to="{ name: 'Ads' }" class="btn btn-sm btn-success mb-0">
          Back to ads
        </router-link>
      </div>
    </div>

    <template v-else-if="creative && ready">
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <router-link
            :to="{ name: 'Ads' }"
            class="text-xs text-secondary text-decoration-none"
          >
            <i class="fas fa-arrow-left me-2"></i>All ads
          </router-link>

          <div class="d-flex flex-wrap align-items-start gap-3 mt-3">
            <div class="ad-thumbnail">
              <img v-if="mediaPreviewUrl" :src="mediaPreviewUrl" alt="" />
              <i
                v-else
                class="fas"
                :class="
                  creative.content?.media?.type === 'video'
                    ? 'fa-video'
                    : 'fa-image'
                "
              ></i>
            </div>

            <div class="flex-grow-1">
              <div class="d-flex align-items-center flex-wrap gap-2">
                <h4 class="mb-0">{{ creativeTitle }}</h4>
                <span
                  class="badge text-xxs"
                  :class="
                    creative.enabled
                      ? 'bg-gradient-success'
                      : 'bg-gradient-secondary'
                  "
                >
                  {{ creative.enabled ? "Enabled" : "Disabled" }}
                </span>
              </div>

              <div class="d-flex flex-wrap gap-1 mt-2">
                <span
                  v-for="placement in creative.placements || []"
                  :key="placement"
                  class="badge bg-light text-dark border text-xxs"
                >
                  {{ placementLabel(placement) }}
                </span>
              </div>

              <div class="d-flex flex-wrap gap-3 mt-2 text-xs text-secondary">
                <span>Flight: {{ flightLabel }}</span>
                <span>Created {{ formatDateTime(creative.createdAt) }}</span>
                <span>Weight {{ creative.weight ?? 1 }}</span>
                <span>ID {{ creative.id }}</span>
              </div>
            </div>

            <router-link
              :to="{ name: 'Ads', query: { creative: creative.id } }"
              class="btn btn-sm btn-outline-secondary mb-0"
            >
              <i class="fas fa-pen me-2"></i>Edit ad
            </router-link>
          </div>
        </div>
      </div>

      <AdStatsFilters
        v-model:preset="preset"
        v-model:date-from="dateFrom"
        v-model:date-to="dateTo"
        v-model:timezone="timezone"
        v-model:granularity="granularity"
        v-model:compare="compare"
        :min-date="firstDay"
        :loading="statsLoading"
        @refresh="loadStats"
      />

      <div v-if="statsError" class="card shadow-sm">
        <div class="card-body text-center py-5">
          <p class="text-sm mb-3">{{ statsError }}</p>
          <button
            type="button"
            class="btn btn-sm btn-success mb-0"
            @click="loadStats"
          >
            Try again
          </button>
        </div>
      </div>

      <div v-else-if="!stats" class="card shadow-sm">
        <div class="card-body text-center py-5">
          <div class="spinner-border text-success" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 mb-0 text-sm text-secondary">
            Loading statistics...
          </p>
        </div>
      </div>

      <div v-else :class="{ 'is-refreshing': statsLoading }">
        <div
          class="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-3"
        >
          <h6 class="mb-0">{{ periodLabel }}</h6>
          <span v-if="previousPeriodLabel" class="text-xs text-secondary">
            Compared with {{ previousPeriodLabel }}
          </span>
        </div>

        <div v-if="!hasEvents" class="alert alert-light border text-sm py-2">
          No events were recorded for this ad in the selected period.
        </div>

        <div class="row g-3 mb-4">
          <div
            v-for="card in kpiCards"
            :key="card.key"
            class="col-6 col-md-4 col-xl"
          >
            <AdStatCard
              :label="card.label"
              :value="card.value"
              :hint="card.hint"
              :accent="card.accent"
              :delta="card.delta"
              :good-when="card.goodWhen"
            />
          </div>
        </div>

        <div class="card shadow-sm mb-4">
          <div
            class="card-header pb-0 border-0 d-flex justify-content-between align-items-center flex-wrap gap-2"
          >
            <div>
              <h6 class="mb-1">Performance over time</h6>
              <p class="text-xs text-secondary mb-0">
                {{ stats.range.granularity === "hour" ? "Hourly" : "Daily" }}
                buckets in {{ stats.range.timezone }}
              </p>
            </div>
            <div class="chart-view-tabs">
              <div class="btn-group btn-group-sm" role="group">
                <button
                  v-for="view in availableChartViews"
                  :key="view.key"
                  type="button"
                  class="btn mb-0"
                  :class="
                    view.key === chartView
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                  "
                  @click="chartView = view.key"
                >
                  {{ view.label }}
                </button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <apexchart
              v-if="chart.hasData"
              :key="chartKey"
              :type="chart.options.chart.type"
              height="340"
              :options="chart.options"
              :series="chart.series"
            />
            <div
              v-else
              class="d-flex align-items-center justify-content-center text-sm text-secondary"
              style="height: 220px"
            >
              Nothing to plot for this metric in the selected period.
            </div>
          </div>
        </div>

        <div class="row g-4 mb-4">
          <div class="col-12 col-xl-6">
            <div class="card shadow-sm h-100">
              <div class="card-header pb-0 border-0">
                <h6 class="mb-1">Engagement outcomes</h6>
                <p class="text-xs text-secondary mb-0">
                  What happened after the ad was shown.
                </p>
              </div>
              <div
                class="card-body d-flex flex-column justify-content-evenly"
              >
                <div
                  v-for="row in outcomeRows"
                  :key="row.key"
                  class="outcome-row"
                >
                  <div
                    class="d-flex justify-content-between align-items-baseline"
                  >
                    <span class="text-sm fw-semibold">{{ row.label }}</span>
                    <span class="text-sm">
                      <span class="fw-bold">{{ formatPercent(row.rate) }}</span>
                      <span class="text-xs text-secondary ms-2">
                        {{ formatCount(row.count) }} {{ row.basis }}
                      </span>
                    </span>
                  </div>
                  <div class="meter">
                    <div
                      class="meter-fill"
                      :style="{
                        width: `${Math.min(row.rate * 100, 100)}%`,
                        background: row.color,
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 col-xl-6">
            <div class="card shadow-sm h-100">
              <div class="card-header pb-0 border-0">
                <h6 class="mb-1">Audience &amp; timing</h6>
                <p class="text-xs text-secondary mb-0">
                  Who saw the ad and for how long.
                </p>
              </div>
              <div class="card-body pt-2">
                <dl class="metric-list mb-0">
                  <div
                    v-for="row in audienceRows"
                    :key="row.label"
                    class="metric-item"
                  >
                    <div>
                      <dt class="text-sm fw-semibold">{{ row.label }}</dt>
                      <dd class="text-xs text-secondary mb-0">
                        {{ row.description }}
                      </dd>
                    </div>
                    <span class="metric-value">{{ row.value }}</span>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div class="card shadow-sm mb-2">
          <div class="card-header pb-0 border-0">
            <h6 class="mb-1">Breakdown by placement</h6>
            <p class="text-xs text-secondary mb-0">
              Where the ad was shown and how each placement performed.
            </p>
          </div>
          <div class="card-body">
            <div v-if="!placementRows.length" class="text-sm text-secondary">
              No placement data for the selected period.
            </div>
            <div v-else class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Placement
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      Impressions
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end"
                    >
                      Clicks
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end"
                    >
                      CTR
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end"
                    >
                      Rewards
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end"
                    >
                      Dismissals
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end"
                    >
                      Errors
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in placementRows" :key="row.placement">
                    <td class="text-sm fw-semibold">{{ row.label }}</td>
                    <td class="placement-share-cell">
                      <div class="d-flex justify-content-between text-sm">
                        <span>{{ formatCount(row.impressions) }}</span>
                        <span class="text-xs text-secondary">
                          {{ formatPercent(row.share, 1) }}
                        </span>
                      </div>
                      <div class="meter meter-thin">
                        <div
                          class="meter-fill"
                          :style="{
                            width: `${row.share * 100}%`,
                            background: CHART_COLORS.impressions,
                          }"
                        ></div>
                      </div>
                    </td>
                    <td class="text-sm text-end">
                      {{ formatCount(row.clicks) }}
                    </td>
                    <td class="text-sm text-end">
                      {{ formatPercent(row.clickThroughRate) }}
                    </td>
                    <td class="text-sm text-end">
                      {{ formatCount(row.rewardsEarned) }}
                    </td>
                    <td class="text-sm text-end">
                      {{ formatCount(row.dismissals) }}
                    </td>
                    <td class="text-sm text-end">
                      {{ formatCount(row.errors) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <p class="text-xs text-secondary mt-3 mb-0">
          Events are counted by the time the server received them. Reach and
          frequency count distinct users within the selected period.
        </p>
      </div>
    </template>
  </div>
</template>

<style scoped>
.ad-thumbnail {
  width: 64px;
  height: 64px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 0.75rem;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
  color: #adb5bd;
  font-size: 1.4rem;
}

.ad-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chart-view-tabs {
  max-width: 100%;
  overflow-x: auto;
}

.chart-view-tabs .btn {
  white-space: nowrap;
}

.is-refreshing {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.meter {
  height: 8px;
  margin-top: 0.4rem;
  border-radius: 999px;
  background: #edf0f4;
  overflow: hidden;
}

.meter-thin {
  height: 5px;
  margin-top: 0.3rem;
}

.meter-fill {
  height: 100%;
  min-width: 2px;
  border-radius: 999px;
}

.metric-list {
  display: flex;
  flex-direction: column;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid #edf0f4;
}

.metric-item:last-child {
  border-bottom: 0;
}

.metric-item dt {
  margin: 0;
}

.metric-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #344767;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.placement-share-cell {
  min-width: 180px;
}
</style>
